/*
 * WO-38 — Payments & refunds, shown on Admin → Revenue.
 *
 * Every payment Mid2Bid recorded (first payments, renewals, reports), and a Refund
 * button that gives all or part of it back to the customer's card through Stripe.
 * "End access" (ticked by default) also ends the plan now / removes the report.
 * The admin types REFUND to confirm. A refund Stripe holds as "pending" (Stripe
 * balance too low) is shown as pending, never as done.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Undo2, CreditCard, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminData, ErrorBox, SectionNote } from './AdminKit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v, cur) => `${Number(v || 0).toFixed(2)} ${cur || 'USD'}`;
// WO-43 — the server sends UTC times without a zone mark ("2026-09-22T11:53:00");
// a browser reads those as local time, so every time showed 2 hours early in Spain.
const asUtc = (iso) => (typeof iso === 'string' && !/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? `${iso}Z` : iso);
const when = (iso) => (iso ? new Date(asUtc(iso)).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');

const WHAT = {
  subscription: 'Plan — first payment',
  subscription_renewal: 'Plan — renewal',
  report: 'Report',
};

const STATUS = {
  paid: { label: 'Paid', cls: 'bg-green-500/15 text-green-600' },
  partially_refunded: { label: 'Partly refunded', cls: 'bg-amber-500/15 text-amber-600' },
  refunded: { label: 'Refunded', cls: 'bg-muted text-muted-foreground' },
};

const REFUND_STATUS = {
  succeeded: 'sent to the card',
  pending: 'PENDING — Stripe sends it when the balance covers it',
  requires_action: 'waiting for the customer',
  failed: 'FAILED — not paid back',
  canceled: 'cancelled',
};

function RefundDialog({ payment, onClose, onDone }) {
  const { getToken } = useAuth();
  const [amount, setAmount] = useState('');
  const [endAccess, setEndAccess] = useState(true);
  const [reason, setReason] = useState('requested_by_customer');
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  if (!payment) return null;
  const partial = amount !== '' && Number(amount) < payment.refundable;
  const what = payment.payment_type === 'report'
    ? 'Remove the report from the company\'s library'
    : 'End the plan now and move the account to Free';

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payments/${encodeURIComponent(payment.id)}/refund`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount === '' ? null : Number(amount),
          end_access: endAccess, reason, note: note || null, confirm,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body.detail || `The server answered ${res.status}.`);
      } else {
        (body.status === 'succeeded' ? toast.success : toast.warning)(body.message, { duration: 12000 });
        onDone();
      }
    } catch (e) {
      toast.error('The server could not be reached. Nothing was refunded.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle>Refund this payment</DialogTitle>
          <DialogDescription>
            {payment.buyer_email || 'Unknown buyer'} · {WHAT[payment.payment_type] || payment.payment_type}
            {payment.plan_id ? ` (${payment.plan_id})` : ''}{payment.report_title ? ` — ${payment.report_title}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p>Paid {fmt(payment.paid, payment.currency)} · can still be refunded{' '}
            <strong>{fmt(payment.refundable, payment.currency)}</strong>. The money goes back to the card that paid.</p>
          <label className="block space-y-1">
            <span className="font-medium">Amount ({payment.currency})</span>
            <Input type="number" min="0.01" step="0.01" max={payment.refundable} value={amount}
              placeholder={`Leave empty to refund all ${payment.refundable.toFixed(2)}`}
              onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" />
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={endAccess}
              onChange={(e) => setEndAccess(e.target.checked)} data-testid="refund-end-access" />
            <span><span className="font-medium">{what}</span>
              <span className="block text-muted-foreground">
                Untick for a goodwill refund: the customer keeps what they bought{payment.payment_type === 'report' ? '' : ' and the plan keeps renewing'}.
                {!payment.plan_live && ' (Already ended.)'}
              </span></span>
          </label>
          <label className="block space-y-1">
            <span className="font-medium">Reason</span>
            <select className="w-full rounded-md border bg-background px-3 py-2" value={reason}
              onChange={(e) => setReason(e.target.value)} data-testid="refund-reason">
              <option value="requested_by_customer">The customer asked for it</option>
              <option value="duplicate">Charged twice</option>
              <option value="fraudulent">Fraud — card used without permission</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className="font-medium">Note for the record (optional)</span>
            <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </label>
          <label className="block space-y-1">
            <span className="font-medium">Type REFUND to confirm</span>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="refund-confirm" />
          </label>
          {partial && endAccess && (
            <p className="text-amber-600">A partial refund with “end access” ticked still ends the {payment.payment_type === 'report' ? 'report access' : 'plan'}.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={submit} data-testid="refund-submit"
            disabled={busy || confirm.trim().toUpperCase() !== 'REFUND'}>
            {busy ? 'Refunding…' : 'Refund'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPaymentsRefunds({ onChanged }) {
  const { data, loading, error, reload } = useAdminData('/api/admin/payments');
  const [selected, setSelected] = useState(null);
  const rows = data?.payments || [];

  return (
    <Card className="border-0 bg-card/50" data-testid="admin-payments-refunds">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" />Payments &amp; refunds</CardTitle>
          <CardDescription>
            Every payment Mid2Bid received, newest first{data?.stripe_mode ? ` · Stripe ${data.stripe_mode.toUpperCase()} mode` : ''}
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={reload} aria-label="Reload payments"><RefreshCw className="w-4 h-4" /></Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {error && <ErrorBox message={error} onRetry={reload} />}
        {loading && <p className="text-muted-foreground text-center py-4">Loading…</p>}
        {!loading && !error && rows.length === 0 && (
          <p className="text-muted-foreground text-center py-4">No payment received yet</p>
        )}
        {rows.map((p) => {
          const st = STATUS[p.payment_status] || { label: p.payment_status, cls: 'bg-muted' };
          return (
            <div key={p.id} className="p-3 rounded-lg bg-muted/30 space-y-1" data-testid={`payment-row-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.buyer_email || '—'}{p.company ? ` · ${p.company}` : ''}</p>
                  <p className="text-xs text-muted-foreground">
                    {when(p.created_at)} · {WHAT[p.payment_type] || p.payment_type}
                    {p.plan_id ? ` · ${p.plan_id}${p.billing_cycle ? ` ${p.billing_cycle}` : ''}` : ''}
                    {p.report_title ? ` · ${p.report_title}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{fmt(p.paid, p.currency)}</span>
                  <Badge variant="outline" className={st.cls}>{st.label}</Badge>
                  {p.refundable > 0 && (
                    <Button size="sm" variant="outline" onClick={() => setSelected(p)} data-testid={`refund-btn-${p.id}`}>
                      <Undo2 className="w-4 h-4 mr-1" />Refund
                    </Button>
                  )}
                </div>
              </div>
              {p.refunds.map((r) => (
                <p key={r.id} className="text-xs text-muted-foreground pl-2 border-l-2">
                  Refund {fmt(r.amount, p.currency)} on {when(r.created_at)} — {REFUND_STATUS[r.status] || r.status}
                  {r.access_ended ? ' · access ended' : ''}{r.created_by === 'stripe_dashboard' ? ' · made in Stripe' : ''}
                  {r.failure_reason ? ` (${r.failure_reason})` : ''}
                </p>
              ))}
            </div>
          );
        })}
        <SectionNote>
          A refund comes out of the Stripe balance. If the balance is lower than the refund, Stripe marks it
          pending and sends it by itself as soon as the balance covers it. Stripe keeps its processing fee on refunded payments.
        </SectionNote>
      </CardContent>
      <RefundDialog payment={selected} onClose={() => setSelected(null)}
        onDone={() => { setSelected(null); reload(); if (onChanged) onChanged(); }} />
    </Card>
  );
}
